import React, {useEffect} from 'react';
import {Link as RouterLink, Navigate, useNavigationType, useParams,} from 'react-router-dom';
import {Helmet} from 'react-helmet-async';
// @mui
import {alpha, Box, Button, Card, CardContent, Container, Grid, Stack, Typography} from '@mui/material';
import {ArrowBack, ArrowForward, Done} from "@mui/icons-material";
// config
import {featureConfigs} from '../config/features';
import StageGateVisual from '../components/feature-visuals/StageGateVisual';
import ReviewLoopVisual from '../components/feature-visuals/ReviewLoopVisual';
import ReviewHistoryVisual from '../components/feature-visuals/ReviewHistoryVisual';
import HowItWorksVisual from '../components/feature-visuals/HowItWorksVisual';
import FormulaCompositionVisual from '../components/feature-visuals/FormulaCompositionVisual';
import SmartLabellingVisual from '../components/feature-visuals/SmartLabellingVisual';
import NutritionClaimsVisual from "../components/feature-visuals/NutritionClaimsVisual";
import TechnicalSafetyDataVisual from "../components/feature-visuals/TechnicalSafetyDataVisual";
import AminogramVisual from "../components/feature-visuals/AminogramVisual";
import RegulatoryQAVisual from '../components/feature-visuals/RegulatoryQAVisual';
import PlatformHelpVisual from "../components/feature-visuals/PlatformHelpVisual";
import ContextAwareSupportVisual from "../components/feature-visuals/ContextAwareSupportVisual";
import SourcesTransparencyVisual from "../components/feature-visuals/SourcesTransparencyVisual";
import ManagementPlatformOverviewVisual from "../components/feature-visuals/ManagementPlatformOverviewVisual";
import ManagementFormulationTrackingVisual from "../components/feature-visuals/ManagementFormulationTrackingVisual";
import ManagementIngredientAnalyticsVisual from "../components/feature-visuals/ManagementIngredientAnalyticsVisual";
import ManagementDocsConfigurationVisual from "../components/feature-visuals/ManagementDocsConfigurationVisual";
import SupplierTdsImporterVisual from "../components/feature-visuals/SupplierTdsImporterVisual";
import BulkProductFormulationImporterVisual from "../components/feature-visuals/BulkProductFormulationImporterVisual";
import EnterpriseErpIntegrationVisual from "../components/feature-visuals/EnterpriseErpIntegrationVisual";
import IngredientSearchVisual from '../components/feature-visuals/IngredientSearchVisual';
import IngredientRecordsVisual from '../components/feature-visuals/IngredientRecordsVisual';
import IngredientSourceIdentifiersVisual from "../components/feature-visuals/IngredientSourceIdentifiersVisual";
import IngredientNutritionTechnicalVisual from "../components/feature-visuals/IngredientNutritionTechnicalVisual";
import IngredientAlternativesComparisonVisual from "../components/feature-visuals/IngredientAlternativesComparisonVisual";
// ----------------------------------------------------------------------

const FeatureImagePlaceholder = ({src, title, subtitle, height = 320}) => {
    const [error, setError] = React.useState(false);

    useEffect(() => {
        setError(false);
    }, [src]);

    if (src && !error) {
        return (<Box
            sx={{
                width: '100%',
                height,
                borderRadius: 3,
                bgcolor: 'background.neutral',
                boxShadow: (theme) => `0 24px 48px ${alpha(theme.palette.common.black, 0.1)}`,
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
            }}
        >
            <Box
                component="img"
                src={src}
                alt={title}
                onError={() => setError(true)}
                sx={{
                    width: '100%', height: '100%', objectFit: 'contain', display: 'block',
                }}
            />
        </Box>);
    }

    return (<Box
        sx={{
            height,
            borderRadius: 3,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
            border: '1px dashed',
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.25),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 4,
        }}
    >
        <Box>
            <Typography variant="subtitle1" sx={{fontWeight: 800, mb: 1}}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {subtitle}
            </Typography>
        </Box>
    </Box>);
};

export default function FeatureDetailPage() {
    const {featureSlug} = useParams();
    const config = featureConfigs.find((f) => f.slug === featureSlug);

    const navigationType = useNavigationType();

    useEffect(() => {
        window.history.scrollRestoration = 'auto';
    }, []);

    useEffect(() => {
        if (navigationType !== 'POP') {
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'auto',
            });
        }
    }, [featureSlug, navigationType]);

    if (!config) {
        return <Navigate to="/404"/>;
    }

    const relatedFeatures = featureConfigs.filter((f) => config.related.includes(f.slug) || config.related.includes(f.id));
    const showHowItWorks = false;
    return (<>
        <Helmet>
            <title>{`${config.title} | CibusAI`}</title>
        </Helmet>

        {/* Hero Section */}
        <Box sx={{py: {xs: 8, md: 12}, bgcolor: 'background.neutral'}}>
            <Container>
                <Grid container spacing={8} alignItems="center">
                    <Grid item xs={12} md={5}>
                        <Typography variant="overline" color="primary" sx={{fontWeight: 800, mb: 2, display: 'block'}}>
                            {config.overline}
                        </Typography>
                        <Typography variant="h1" sx={{mb: 3, fontWeight: 800}}>
                            {config.title}
                        </Typography>
                        <Typography variant="h4" color="text.secondary" sx={{mb: 5, fontWeight: 400, lineHeight: 1.5}}>
                            {config.description}
                        </Typography>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                            <Button
                                variant="contained"
                                size="large"
                                component="a"
                                href="mailto:info@omniumai.com"
                                sx={{px: 4, py: 1.5, fontSize: '1.1rem'}}
                            >
                                Book a demo
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                component={RouterLink}
                                to={`/home?feature=${config.id}#features`}
                                startIcon={<ArrowBack/>}
                                sx={{px: 4, py: 1.5, fontSize: '1.1rem'}}
                            >
                                Back to overview
                            </Button>
                        </Stack>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={7}
                        sx={{
                            display: {xs: 'none', md: 'block'},
                        }}
                    >
                        <FeatureImagePlaceholder
                            src={config.image}
                            title={config.heroPlaceholder}
                            subtitle="Feature visualization"
                            height={480}
                        />
                    </Grid>
                </Grid>
            </Container>
        </Box>

        {/* Benefits Section */}
        <Box sx={{py: {xs: 8, md: 11}, borderTop: '1px solid', borderColor: 'divider'}}>
            <Container>
                <Box sx={{mb: 8, textAlign: 'center'}}>
                    <Typography variant="h2" sx={{mb: 3}}>What you can do with this feature</Typography>
                </Box>
                <Grid container spacing={4}>
                    {config.benefits.map((benefit, index) => (<Grid item xs={12} md={4} key={index}>
                        <Card sx={{height: '100%', border: '1px solid', borderColor: 'divider', boxShadow: 'none'}}>
                            <CardContent sx={{p: 4}}>
                                <Typography variant="h5" sx={{mb: 2, fontWeight: 800}}>{benefit.title}</Typography>
                                <Typography variant="body1" color="text.secondary">{benefit.description}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>))}
                </Grid>
            </Container>
        </Box>

        {/*/!* Capabilities Section *!/*/}
        {/*<Box sx={{py: {xs: 10, md: 15}, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02), borderTop: '1px solid', borderColor: 'divider'}}>*/}
        {/*    <Container>*/}
        {/*        <Box sx={{mb: 8, textAlign: 'center'}}>*/}
        {/*            <Typography variant="h2" sx={{mb: 3}}>Key capabilities</Typography>*/}
        {/*        </Box>*/}
        {/*        <Grid container spacing={4}>*/}
        {/*            {config.capabilities.map((cap, index) => (<Grid item xs={12} sm={6} md={4} key={index}>*/}
        {/*                <Stack direction="row" spacing={2.5}>*/}
        {/*                    <Box sx={{color: 'primary.main', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08), p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'fit-content'}}>*/}
        {/*                        {React.cloneElement(cap.icon, {sx: {fontSize: 28}})}*/}
        {/*                    </Box>*/}
        {/*                    <Box>*/}
        {/*                        <Typography variant="h6" sx={{mb: 1, fontWeight: 800}}>{cap.title}</Typography>*/}
        {/*                        <Typography variant="body2" color="text.secondary">{cap.description}</Typography>*/}
        {/*                    </Box>*/}
        {/*                </Stack>*/}
        {/*            </Grid>))}*/}
        {/*        </Grid>*/}
        {/*    </Container>*/}
        {/*</Box>*/}

        {/* How it Works Section */}
        {showHowItWorks && (<Box sx={{py: {xs: 10, md: 15}, borderTop: '1px solid', borderColor: 'divider'}}>
            <Container>
                <Box sx={{mb: 8, textAlign: 'center'}}>
                    <Typography variant="h2" sx={{mb: 3}}>How it works</Typography>
                </Box>
                <Grid container spacing={4}>
                    {config.howItWorks.map((step, index) => (<Grid item xs={12} md={4} key={index}>
                        <Stack spacing={3}>
                            <Box sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 800, fontSize: '1.25rem'
                            }}>
                                {index + 1}
                            </Box>
                            <Box>
                                <Typography variant="h5" sx={{mb: 2, fontWeight: 800}}>{step.title}</Typography>
                                <Typography variant="body1" color="text.secondary"
                                            sx={{mb: 4}}>{step.description}</Typography>
                                <HowItWorksVisual type={step.visual}/>
                            </Box>
                        </Stack>
                    </Grid>))}
                </Grid>
            </Container>
        </Box>)}

        {/* Subfeatures Section */}
        {/*<Box sx={{py: {xs: 10, md: 15}, bgcolor: 'background.neutral', borderTop: '1px solid', borderColor: 'divider'}}>*/}
        {/*    <Container>*/}
        {/*        <Box sx={{mb: 8, textAlign: 'center'}}>*/}
        {/*            <Typography variant="h2" sx={{mb: 3}}>Deeper dive into {config.overline.toLowerCase()}</Typography>*/}
        {/*        </Box>*/}
        {/*        <Grid container spacing={4}>*/}
        {/*            {config.subfeatures.map((sub, index) => (<Grid item xs={12} md={6} key={index}>*/}
        {/*                    <Card sx={{borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden'}}>*/}
        {/*                        <Grid container>*/}
        {/*                            <Grid item xs={12} sm={5}>*/}
        {/*                                <Box*/}
        {/*                                    sx={{*/}
        {/*                                        height: '100%',*/}
        {/*                                        minHeight: 200,*/}
        {/*                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),*/}
        {/*                                        borderRight: {sm: '1px solid'},*/}
        {/*                                        borderColor: 'divider',*/}
        {/*                                        display: 'flex',*/}
        {/*                                        alignItems: 'stretch',*/}
        {/*                                        justifyContent: 'center',*/}
        {/*                                        overflow: 'hidden',*/}
        {/*                                    }}*/}
        {/*                                >*/}
        {/*                                    <Box*/}
        {/*                                        component="img"*/}
        {/*                                        src={sub.imageCrop}*/}
        {/*                                        alt={sub.imageAlt || sub.title}*/}
        {/*                                        sx={{*/}
        {/*                                            width: '100%', height: '100%', minHeight: 200, objectFit: 'contain', display: 'block',*/}
        {/*                                        }}*/}
        {/*                                    />*/}
        {/*                                </Box>*/}
        {/*                            </Grid>*/}
        {/*                            <Grid item xs={12} sm={7}>*/}
        {/*                                <CardContent sx={{p: 4}}>*/}
        {/*                                    <Typography variant="h5" sx={{mb: 1.5, fontWeight: 800}}>{sub.title}</Typography>*/}
        {/*                                    <Typography variant="body2" color="text.secondary" sx={{mb: 2.5}}>{sub.description}</Typography>*/}
        {/*                                    <Stack spacing={1}>*/}
        {/*                                        {sub.bullets.map((bullet, i) => (<Stack key={i} direction="row" spacing={1} alignItems="center">*/}
        {/*                                                <Done sx={{color: 'primary.main', fontSize: 16}}/>*/}
        {/*                                                <Typography variant="caption" sx={{fontWeight: 600}}>{bullet}</Typography>*/}
        {/*                                            </Stack>))}*/}
        {/*                                    </Stack>*/}
        {/*                                </CardContent>*/}
        {/*                            </Grid>*/}
        {/*                        </Grid>*/}
        {/*                    </Card>*/}
        {/*                </Grid>))}*/}
        {/*        </Grid>*/}
        {/*    </Container>*/}
        {/*</Box>*/}

        {/* Subfeatures Section */}
        <Box sx={{py: {xs: 8, md: 10}, bgcolor: 'background.neutral', borderTop: '1px solid', borderColor: 'divider'}}>
            <Container>
                <Box sx={{mb: 8, textAlign: 'center'}}>
                    <Typography variant="h2" sx={{mb: 3}}>Deeper dive into {config.overline.toLowerCase()}</Typography>
                </Box>
                <Grid container spacing={4}>
                    {config.subfeatures.map((sub, index) => (<Grid item xs={12} md={12} key={index}>
                        <Card sx={{borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden'}}>
                            <Grid container>
                                <Grid item xs={12} sm={6.5}>
                                    <Box
                                        sx={{
                                            height: '100%',
                                            minHeight: 200,
                                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
                                            borderRight: {sm: '1px solid'},
                                            borderColor: 'divider',
                                            display: 'flex',
                                            alignItems: 'stretch',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {sub.visual === 'stage-gate' ? (<StageGateVisual/>) : sub.visual === 'review-loop' ? (<ReviewLoopVisual/>) : sub.visual === 'review-history' ? (<ReviewHistoryVisual/>) : sub.visual === 'formula-composition' ? (
                                            <FormulaCompositionVisual/>) : sub.visual === 'smart-labelling' ? (<SmartLabellingVisual/>) : sub.visual === 'nutrition-claims' ? (<NutritionClaimsVisual/>) : sub.visual === 'technical-safety-data' ? (
                                            <TechnicalSafetyDataVisual/>) : sub.visual === 'aminogram' ? (<AminogramVisual/>) : sub.visual === 'regulatory-qa' ? (<RegulatoryQAVisual/>) : sub.visual === 'platform-help' ? (
                                            <PlatformHelpVisual/>) : sub.visual === 'context-aware-support' ? (<ContextAwareSupportVisual/>) : sub.visual === 'sources-transparency' ? (<SourcesTransparencyVisual/>) : sub.visual === 'management-platform-overview' ? (
                                            <ManagementPlatformOverviewVisual/>) : sub.visual === 'management-formulation-tracking' ? (<ManagementFormulationTrackingVisual/>) : sub.visual === 'management-ingredient-analytics' ? (
                                            <ManagementIngredientAnalyticsVisual/>) : sub.visual === 'management-docs-configuration' ? (<ManagementDocsConfigurationVisual/>) : sub.visual === 'supplier-tds-importer' ? (
                                            <SupplierTdsImporterVisual/>) : sub.visual === 'bulk-product-formulation-importer' ? (<BulkProductFormulationImporterVisual/>) : sub.visual === 'enterprise-erp-integration' ? (
                                            <EnterpriseErpIntegrationVisual/>) : sub.visual === 'ingredient-search' ? (<IngredientSearchVisual/>) : sub.visual === 'ingredient-records' ? (<IngredientRecordsVisual/>) : sub.visual === 'ingredient-source-identifiers' ? (
                                            <IngredientSourceIdentifiersVisual/>) : sub.visual === 'ingredient-nutrition-technical' ? (<IngredientNutritionTechnicalVisual/>) : sub.visual === 'ingredient-alternatives-comparison' ? (
                                            <IngredientAlternativesComparisonVisual/>) : (<Box
                                            component="img"
                                            src={sub.image}
                                            alt={sub.title}
                                            sx={{
                                                width: '100%', height: '100%', objectFit: 'contain', display: 'block',
                                            }}
                                        />)}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={5.5}>
                                    <CardContent sx={{p: 4}}>
                                        <Typography variant="h5"
                                                    sx={{mb: 1.5, fontWeight: 800}}>{sub.title}</Typography>
                                        <Typography variant="body2" color="text.secondary"
                                                    sx={{mb: 2.5}}>{sub.description}</Typography>
                                        <Stack spacing={1}>
                                            {sub.bullets.map((bullet, i) => (<Stack key={i} direction="row" spacing={1} alignItems="center">
                                                <Done sx={{color: 'primary.main', fontSize: 16}}/>
                                                <Typography variant="caption"
                                                            sx={{fontWeight: 600}}>{bullet}</Typography>
                                            </Stack>))}
                                        </Stack>
                                    </CardContent>
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>))}
                </Grid>
            </Container>
            <Box sx={{display: 'flex', justifyContent: 'center', mt: 4, mb: 2}}>
                <Button
                    variant="outlined"
                    size="large"
                    component={RouterLink}
                    to={`/home?feature=${config.id}#features`}
                    startIcon={<ArrowBack/>}
                    sx={{px: 4, py: 1.5, fontSize: '1.1rem'}}
                >
                    Back to overview
                </Button>
            </Box>
        </Box>


        {/* Related Features Section */}
        <Box
            sx={{
                py: {xs: 10, md: 15}, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
            }}
        >
            <Container>
                <Box sx={{mb: 6}}>
                    <Typography variant="h3" sx={{fontWeight: 800}}>
                        Explore related features
                    </Typography>
                </Box>

                <Grid container spacing={4}>
                    {relatedFeatures.map((rel, index) => (<Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        key={index}
                        sx={{display: 'flex'}}
                    >
                        <Card
                            component={RouterLink}
                            to={`/features/${rel.slug}`}
                            sx={{
                                bgcolor: 'transparent', boxShadow: 'none', width: '100%', height: '100%', textDecoration: 'none', borderRadius: 2, overflow: 'hidden', transition: 'box-shadow 0.2s ease, transform 0.2s ease',

                                '&:hover': {
                                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.10)', transform: 'translateY(-2px)',
                                },

                                '&:hover .related-card-content': {
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.10),
                                },
                            }}
                        >
                            <CardContent
                                className="related-card-content"
                                sx={{
                                    p: 3, height: '100%', transition: 'background-color 0.2s ease',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    sx={{mb: 2}}
                                >
                                    <Box sx={{color: 'primary.main'}}>
                                        {React.cloneElement(rel.icon, {
                                            sx: {fontSize: 24},
                                        })}
                                    </Box>

                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 800, color: 'text.primary',
                                        }}
                                    >
                                        {rel.title}
                                    </Typography>
                                </Stack>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{mb: 2}}
                                >
                                    {rel.description}
                                </Typography>

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{color: 'primary.main'}}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{fontWeight: 800}}
                                    >
                                        Learn more
                                    </Typography>

                                    <ArrowForward sx={{fontSize: 16}}/>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>))}
                </Grid>
            </Container>
        </Box>

        {/* Final CTA Section */}
        <Box
            sx={{
                py: {xs: 8, md: 11}, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.neutral',
            }}
        >
            <Container maxWidth="lg">
                <Card
                    sx={{
                        borderRadius: 4, border: '1px solid', borderColor: (theme) => alpha(theme.palette.primary.main, 0.14), boxShadow: 'none', bgcolor: 'background.paper', overflow: 'hidden',
                    }}
                >
                    <CardContent
                        sx={{
                            p: {xs: 4, md: 6}, textAlign: 'center', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
                        }}
                    >
                        <Typography variant="overline" color="primary" sx={{fontWeight: 800, mb: 1.5, display: 'block'}}>
                            Book a demo
                        </Typography>

                        <Typography variant="h2" sx={{mb: 2.5, fontWeight: 800}}>
                            Ready to see how CibusAI fits your R&D workflow?
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{
                                mb: 4, maxWidth: 680, mx: 'auto', color: 'text.primary', opacity: 0.72, lineHeight: 1.7,
                            }}
                        >
                            Book a short walkthrough to explore how CibusAI can support your formulation,
                            ingredient and product development workflows.
                        </Typography>

                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} justifyContent="center">
                            <Button
                                variant="contained"
                                size="large"
                                component="a"
                                href="mailto:info@omniumai.com"
                                sx={{
                                    px: 5, py: 1.5, fontSize: '1.05rem',
                                }}
                            >
                                Book a personalised demo
                            </Button>

                            <Button
                                variant="outlined"
                                size="large"
                                component={RouterLink}
                                to={`/home?feature=${config.id}#features`}
                                sx={{
                                    px: 5, py: 1.5, fontSize: '1.05rem',
                                }}
                            >
                                Explore all features
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    </>);
}
